/**
 * Formats a phone number for display.
 * - 10 digits: (XXX) XXX-XXXX
 * - 11 digits starting with 1: +1 (XXX) XXX-XXXX
 * - Other formats: returned as-is
 */
export const formatPhoneNumber = (phone: string | undefined): string => {
  if (!phone) return "";
  const cleaned = phone.replace(/\D/g, "");

  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  if (cleaned.length === 11 && cleaned[0] === "1") {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  return phone;
};

/**
 * The user's own phone number, as a device-link target.
 *
 * `isDefault` first, then array order. The handle array's order is not defined
 * and an account accumulates several numbers over time, including ones that are
 * not the user's own device. Picking the wrong one mints a WhatsApp pairing
 * code for a JID the user doesn't control, which then fails at entry with
 * nothing on screen to explain why — so the rule lives here rather than being
 * re-derived per surface. Mirrors the server's `getUserContactPrimaryPhoneHandle`.
 */
export const primaryPhoneHandleValue = (
  handles: readonly { type: string; value: string; isDefault?: boolean }[] | undefined,
): string => {
  const phones = (handles ?? []).filter((handle) => handle.type === "phoneNumber");
  return (phones.find((handle) => handle.isDefault) ?? phones[0])?.value ?? "";
};

/**
 * The number the account receives texts on: its verified phone handle, or
 * undefined when it has none. A change abandoned before its code verified can
 * leave a handle that was never proven, and Settings must not show that as
 * the user's number.
 */
export const verifiedPhoneHandleValue = (
  handles: readonly { type: string; value: string; verified?: boolean }[] | undefined,
): string | undefined =>
  handles?.find((handle) => handle.type === "phoneNumber" && handle.verified)?.value;
